import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Menu } from "semantic-ui-react";

import { Message } from "components";
import { useRegisterNavAction } from "hooks";
import { DealerInvoiceDisabledMessage, DealerInvoiceHistoryTable, MonthlyTable, OnceTable } from "modules/DealerLicenseOverview/components";
import { DEALER_LICENSE_SUBPAGE, useDealerLicenseTabs } from "modules/DealerLicenseOverview/components/DealerLicenseTabs/hooks";
import { setPaginatedInvoices, useDealerLicensesDashboardFilters } from "modules/DealerLicenseOverview/hooks";
import "modules/LocationLicenseOverview/LocationLicenseOverview.scss";

import type { QueryObserverResult } from "@tanstack/react-query";
import type { Licenses } from "modules/LocationLicenseOverview/components";
import type { HistoricalInvoice } from "modules/LocationLicenseOverview/queryKeys";
import type { Dispatch, SetStateAction } from "react";

type Pane = {
  menuItem: string;
  content: React.ReactNode;
};

type DealerLicenseTabsProps = {
  isLoadingDealerLicenses: boolean;
  licenses?: Licenses;
  setInvoicePDF: Dispatch<SetStateAction<Blob | null>>;
  setSelectedInvoice: Dispatch<SetStateAction<HistoricalInvoice | null>>;
  refetchDealerLicenses: () => Promise<QueryObserverResult<Licenses, Error>>;
};

export const DealerLicenseTabs = ({ setSelectedInvoice, setInvoicePDF, refetchDealerLicenses, isLoadingDealerLicenses, licenses }: DealerLicenseTabsProps) => {
  const { t } = useTranslation();

  const {
    refetchInvoices,
    handleChangeTab,
    onInvoiceRowClick,
    invoicePageChange,
    handleResetState,
    dealerInvoices,
    currentInvoicePage,
    errorDealerInvoices,
    isLoadingDealerInvoices,
    isDealerInvoicesRefetching,
    currentLocationLicenseSubpage
  } = useDealerLicenseTabs({ setInvoicePDF, setSelectedInvoice });
  const { dealer } = useDealerLicensesDashboardFilters();

  useRegisterNavAction(() => {
    if (currentLocationLicenseSubpage === DEALER_LICENSE_SUBPAGE.Invoices) {
      setPaginatedInvoices({ data: [] });
      refetchInvoices();
      return;
    }

    refetchDealerLicenses();
  });

  useEffect(() => {
    return () => handleResetState();
  }, []);

  if (errorDealerInvoices) return <Message content={<span className="error-text">{t("v8_failed_to_load_invoices", "Failed to load invoices")}.</span>} />;

  const isDealerLevelInvoicingEnabled = Boolean(dealer?.is_exact_invoicing_enabled && dealer.exact_account_id);
  if (!isDealerLevelInvoicingEnabled) return <DealerInvoiceDisabledMessage />;

  const tabPanes: Pane[] = [
    { menuItem: t("v8_monthly", "Monthly"), content: <MonthlyTable licenses={licenses} isLoadingDealerLicenses={isLoadingDealerLicenses} /> },
    { menuItem: t("v8_once", "Once"), content: <OnceTable licenses={licenses} isLoadingDealerLicenses={isLoadingDealerLicenses} /> },
    {
      menuItem: t("v8_invoice_history", "Invoice History"),
      content: (
        <DealerInvoiceHistoryTable
          invoices={dealerInvoices}
          isDealerInvoicesRefetching={isDealerInvoicesRefetching}
          currentInvoicePage={currentInvoicePage}
          isLoadingInvoices={isLoadingDealerInvoices}
          onPageChange={invoicePageChange}
          onInvoiceRowClick={onInvoiceRowClick}
        />
      )
    }
  ];

  return (
    <>
      <div className="LocationLicenseOverview-Header">
        <div className="LocationLicenseOverview-Header-Tabs">
          <Menu secondary>
            {tabPanes.map((pane, index) => (
              <Menu.Item
                key={index}
                name={pane.menuItem}
                className="location-overview-header-tab"
                active={currentLocationLicenseSubpage === index}
                index={index}
                onClick={handleChangeTab}
              />
            ))}
          </Menu>
        </div>
      </div>
      <div className="LocationLicenseOverview-container mt-20">{tabPanes[currentLocationLicenseSubpage].content}</div>
    </>
  );
};
